import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  PageBarSidebarTrigger,
  SidebarTriggerClaimedProvider,
} from '@cotera/client/v0/components/ui/page-bar-sidebar-trigger';
import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import { CrumbLabel, CurrentCrumb } from './editable-crumb';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { DropdownMenuItem } from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  CollapsedCrumbMenu,
  CrumbSeparator,
  crumbDepthIndent,
} from '@cotera/client/v0/components/ui/breadcrumb-chrome';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from './type/resource-type-config';
import { ContentScrollProvider } from '../shell/content-scroll-context';
import { useIsBelowSm } from '@cotera/client/app/hooks/use-breakpoint';
import {
  useArtifactPanel,
  usePanelBarActionsSlot,
  usePanelBarTitleSlot,
  usePanelUnifiedChrome,
  useUnifiedBarRendered,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { ArtifactPanelExpandButton } from '@cotera/client/v0/chat/artifact-panel-expand-button';
import { UnifiedPanelBar } from '@cotera/client/v0/chat/unified-panel-bar';
import { ArtifactResourceLink, type ResourceTarget } from './navigation';
import { ArtifactLink } from './navigation/artifact-link';
import { useFolderAncestors } from '../folder/use-folder-ancestors';
import { useResourceNavigator } from './navigation/use-resource-navigator';
import { resolveArtifactLink } from './navigation/artifact-links';
import {
  ResourcePageCardDrawnProvider,
  useResourcePageCardDrawn,
  type ResourcePageShellLayout,
  type ResourcePageSurface,
} from './resource-page-framing';

type ResourceMeta = {
  folderId: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  permission: 'view' | 'edit' | 'admin' | 'pending';
};

type FolderData = {
  id: string;
  name: string;
  scope: 'user' | 'org' | 'external';
};

function isResultLike(
  value: unknown
): value is { isErr: () => boolean; value: unknown } {
  if (value === null || value === undefined || typeof value !== 'object') {
    return false;
  }
  return (
    'isErr' in value &&
    typeof (value as { isErr?: unknown }).isErr === 'function' &&
    'value' in value
  );
}

function extractResourceMeta(data: unknown): ResourceMeta | null {
  if (data === null || data === undefined) {
    return null;
  }

  if (isResultLike(data)) {
    if (data.isErr()) {
      return null;
    }
    const value = data.value;
    if (value === null || value === undefined || typeof value !== 'object') {
      return null;
    }
    if ('type' in value && (value as { type: string }).type === 'folder') {
      return null;
    }
    return (
      ('meta' in value
        ? (value as { meta?: ResourceMeta | null }).meta
        : null) ?? null
    );
  }

  if ('meta' in (data as Record<string, unknown>)) {
    const meta = (data as { meta?: ResourceMeta | null }).meta;
    return meta ?? null;
  }

  if ('folderId' in (data as Record<string, unknown>)) {
    return data as ResourceMeta;
  }

  return null;
}

function extractFolderData(data: unknown): FolderData | null {
  if (data === null || data === undefined || typeof data !== 'object') {
    return null;
  }

  if (isResultLike(data)) {
    if (data.isErr()) {
      return null;
    }
    return extractFolderData(data.value);
  }

  if (
    'type' in data &&
    (data as { type: string }).type === 'folder' &&
    'folder' in data
  ) {
    return (data as { folder?: FolderData }).folder ?? null;
  }

  if (
    'id' in data &&
    'name' in data &&
    'scope' in data &&
    typeof (data as { id?: unknown }).id === 'string'
  ) {
    return data as FolderData;
  }

  return null;
}

export type BreadcrumbItem = {
  label: string;
  /** Omit for the last (current-page) crumb. */
  href?: string;
  /**
   * In-page navigation instead of an `href` (e.g. returning from a drill-in to
   * the graph). Rendered as a button; ignored for the last crumb. Takes
   * precedence over `href` when both are set.
   */
  onClick?: () => void;
  /**
   * The workspace resource this crumb points at. When set, the crumb renders as
   * an {@link ArtifactResourceLink}, so clicking it from inside an artifact panel
   * opens the resource as a sibling artifact (staying in the chat) instead of
   * navigating the whole app to its page; ⌘/ctrl-click still opens the full page.
   * Ignored when `onClick` is set.
   */
  target?: ResourceTarget;
  /**
   * Marks the root "Library"/"Shared" crumb. The Library is the root of the tree,
   * not a resource (it has no id, so it can't use `target`), so clicking it from
   * inside an artifact panel pushes it as an in-stack {@link ArtifactLink} view
   * (Back returns here) rather than opening a new tab; ⌘/ctrl-click still opens
   * the `/folders` page. Ignored when `onClick` or `target` is set.
   */
  opensLibrary?: boolean;
  /** Optional emoji shown before the label. */
  emoji?: string | null;
  /**
   * A crumb whose label is still resolving, drawn as a skeleton chip.
   *
   * Lets a trail show the parts it already knows instead of withholding all of
   * it. A resource page knows its own name from the moment it mounts; only the
   * folder ancestry needs a round-trip, and blanking the whole bar for that put
   * a two-chip skeleton in the header on every navigation.
   */
  pending?: boolean;
};

/**
 * Builds breadcrumb items for a resource detail page, resolving folder
 * membership from the React Query cache (or fetching it when not cached).
 *
 * Returns `undefined` while loading so the shell can show a skeleton.
 *
 * Breadcrumb logic:
 *  - No folder      →  [Resource Type Plural] > [Name]
 *  - Private folder →  [Folder Name] > [Name]
 *  - Shared folder  →  Shared > [Folder Name] > [Name]
 *
 * @param folderId  `undefined` = needs to be fetched from the API;
 *                  `null`      = resource has no folder;
 *                  string      = known folder ID (folder name is fetched).
 */
export function useResourceBreadcrumbs({
  resourceType,
  resourceId,
  resourceName,
  resourceEmoji,
  folderId,
  folderScope,
  /** When set, overrides the unfiled list crumb (e.g. workflow-backed scheduled runs under `/triggers`). */
  listBreadcrumb,
}: {
  resourceType: NonFolderResourceType;
  resourceId: string;
  resourceName: string | null;
  resourceEmoji?: string | null;
  folderId?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  listBreadcrumb?: { label: string; href: string };
}): BreadcrumbItem[] | undefined {
  const client = useTenantedClient();
  const org = useWhoami((s) => s.org);
  const { id: orgId, slug: orgSlug } = org;
  const config = RESOURCE_TYPE_CONFIG[resourceType];
  // Inside the artifact panel the unified bar draws the trail, and whatever this
  // returns is discarded. Every resource in the panel was still paying two
  // React Query round-trips to resolve a folder nobody would see, so resolve
  // nothing there.
  const inArtifactPanel = usePanelUnifiedChrome() !== null;

  const needsFetch = folderId === undefined;
  const canFetch = resourceId !== '' && !inArtifactPanel;

  // Step 1: fetch resource meta to resolve folderId when it is not provided.
  // Uses the same cache key as the individual resource query (e.g. useAgentQuery),
  // so this is free when the page has already fetched that resource.
  const { data: resourceGetData, isLoading: metaLoading } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: resourceType,
      id: resourceId,
    }),
    queryFn: () =>
      client.resource.get({
        type: resourceType,
        id: resourceId,
      }),
    enabled: needsFetch && canFetch,
    staleTime: 60_000,
  });
  const resourceMeta = extractResourceMeta(resourceGetData);

  // resolvedFolderId:
  //   undefined  = still loading
  //   null       = no folder
  //   string     = folder ID
  const resolvedFolderId: string | null | undefined = needsFetch
    ? metaLoading
      ? undefined
      : resourceMeta?.folderId ?? null
    : folderId;

  const resolvedFolderScope = folderScope ?? resourceMeta?.folderScope ?? null;

  const hasFolderId =
    resolvedFolderId !== null && resolvedFolderId !== undefined;

  // Step 2: fetch folder name when we have a folderId.
  const { data: folderGetData, isLoading: folderLoading } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Folder,
      id: resolvedFolderId ?? '',
    }),
    queryFn: () => {
      if (resolvedFolderId === null || resolvedFolderId === undefined) {
        return Promise.resolve(null);
      }
      return client.resource.get({
        type: ResourceType.Folder,
        id: resolvedFolderId,
      });
    },
    enabled: hasFolderId && !inArtifactPanel,
    staleTime: 60_000,
  });
  const folderData = extractFolderData(folderGetData);

  const effectiveFolderScope = resolvedFolderScope ?? folderData?.scope ?? null;

  // Step 3: walk the folder's own parents. Without this the trail showed a
  // single folder however deep the resource actually sat — `Acme › My chat` for
  // something in `Library › Clients › Acme` — while the folder *contents* page
  // rendered the full chain from the same data. Two trails, one hierarchy.
  const { ancestors, isLoading: ancestorsLoading } = useFolderAncestors(
    inArtifactPanel ? null : resolvedFolderId
  );

  return useMemo((): BreadcrumbItem[] | undefined => {
    if (inArtifactPanel) {
      // Not `undefined` — that means "still loading" and would put a skeleton
      // in a bar this shell is not drawing.
      return [];
    }

    const name = resourceName ?? 'Untitled';

    const nameCrumb: BreadcrumbItem = {
      label: name,
      emoji: resourceEmoji ?? null,
    };

    // The page knows its own name immediately; only the ancestry needs the
    // network. Show the name with a placeholder in front of it rather than
    // blanking the whole bar, which put a skeleton in the header on every
    // navigation between resources.
    const resolving: BreadcrumbItem[] = [
      { label: '', pending: true },
      nameCrumb,
    ];
    if (resolvedFolderId === undefined) {
      return resolving;
    }
    if (hasFolderId && (folderLoading || ancestorsLoading)) {
      return resolving;
    }

    if (resolvedFolderId === null) {
      // Chats live in the chats stream, not the Library, so they keep pointing at
      // their list page. Every other unfiled resource's home is the Library.
      const underLibrary = resourceType !== ResourceType.Chat;
      if (listBreadcrumb !== undefined) {
        return [listBreadcrumb, nameCrumb];
      }
      if (!underLibrary) {
        return [
          {
            label: `${config.label}s`,
            href: `/${orgSlug}/${config.listRouteSegment}`,
          },
          nameCrumb,
        ];
      }
      const libraryCrumb: BreadcrumbItem = {
        label: 'Library',
        href: `/${orgSlug}/folders`,
        opensLibrary: true,
      };
      return [libraryCrumb, nameCrumb];
    }

    const folderName = folderData?.name ?? 'Folder';
    const isShared =
      effectiveFolderScope === 'org' || effectiveFolderScope === 'external';

    // Folders render as artifacts, so these crumbs open in-panel (via
    // ArtifactResourceLink) rather than tearing the user out of the chat/editor
    // hosting this resource. The link derives its own href from the target.
    const folderCrumbFor = (folder: {
      id: string;
      name: string;
    }): BreadcrumbItem => ({
      label: folder.name,
      target: {
        resourceType: ResourceType.Folder,
        resourceId: folder.id,
        name: folder.name,
      },
    });

    // The root is present whether or not the folder is shared. It used to hang
    // off `isShared`, so anything in a private folder had no crumb back to the
    // Library at all — the trail's root appeared or vanished based on who else
    // could see the folder, which is unrelated to where it sits.
    const rootCrumb: BreadcrumbItem = {
      label: isShared ? 'Shared' : 'Library',
      href: `/${orgSlug}/folders`,
      opensLibrary: true,
    };

    return [
      rootCrumb,
      ...ancestors.map(folderCrumbFor),
      folderCrumbFor({ id: resolvedFolderId, name: folderName }),
      nameCrumb,
    ];
  }, [
    inArtifactPanel,
    ancestors,
    ancestorsLoading,
    resolvedFolderId,
    effectiveFolderScope,
    hasFolderId,
    folderLoading,
    folderData,
    resourceName,
    resourceEmoji,
    config,
    resourceType,
    orgSlug,
    listBreadcrumb,
  ]);
}

/** Back control for drill-in views; pass as `leading` on ResourcePageShell. */
export const ResourcePageShellBackButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    className="size-8 shrink-0 p-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground"
    onClick={onClick}
    aria-label="Back"
  >
    <ChevronLeft className="size-4" />
  </Button>
);

const BreadcrumbSkeleton: React.FC = () => (
  <>
    <Skeleton className="h-3.5 w-16 rounded bg-coco-accent" />
    <CrumbSeparator />
    <Skeleton className="h-3.5 w-28 rounded bg-coco-accent" />
  </>
);

const CRUMB_LINK_CLASS = cn(
  // `max-w-[120px]` on a phone: 180px is half the width of a 375px screen for
  // one ancestor folder, spent ahead of the crumb that names the page.
  'text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 shrink-0 max-w-[120px] sm:max-w-[180px] rounded',
  FOCUS_RING
);

/**
 * How many crumbs stay on the bar. The first (the root) and the last
 * {@link VISIBLE_TAIL_CRUMBS} always render; anything between them folds into a
 * `…` menu that lists the full path, so a resource sitting four folders deep
 * can't push its own name off the end of the bar.
 */
const MAX_VISIBLE_CRUMBS = 4;
const VISIBLE_TAIL_CRUMBS = 2;

/**
 * Activating a crumb without a click on its anchor — what the collapsed menu
 * needs, since a menu row is driven by Radix's `onSelect` (mouse *and*
 * keyboard) rather than by the link inside it.
 *
 * Mirrors what `ArtifactResourceLink` and `ArtifactLink` do on a plain click, so
 * a folder picked out of the menu opens in the panel when there is one instead
 * of navigating the whole app away from it.
 */
const useCrumbActivate = (): ((crumb: BreadcrumbItem) => void) => {
  const navigate = useNavigate();
  const resourceNavigator = useResourceNavigator();
  const artifactPanel = useArtifactPanel();
  const orgSlug = useWhoami((s) => s.org.slug);

  return React.useCallback(
    (crumb: BreadcrumbItem) => {
      if (crumb.onClick !== undefined) {
        crumb.onClick();
        return;
      }
      if (crumb.target !== undefined) {
        resourceNavigator.open(crumb.target);
        return;
      }
      if (crumb.opensLibrary === true) {
        const { href, entry } = resolveArtifactLink(
          { kind: 'library' },
          orgSlug
        );
        // `pushView` targets the focused artifact, so with none open there is
        // nothing to push onto and the crumb has to navigate instead.
        if (artifactPanel !== null && artifactPanel.activeArtifactId !== null) {
          artifactPanel.pushView(entry);
          return;
        }
        navigate(href);
        return;
      }
      if (crumb.href !== undefined) {
        navigate(crumb.href);
      }
    },
    [navigate, resourceNavigator, artifactPanel, orgSlug]
  );
};

/**
 * The `…` crumb standing in for folder levels that don't fit on the bar.
 *
 * Rows navigate from `onSelect` rather than wrapping a link with `asChild`:
 * Radix's roving focus needs a ref on whatever it clones, and none of the crumb
 * link components forward one, so `asChild` left it focusing `null`.
 */
const CollapsedCrumbs: React.FC<{ items: BreadcrumbItem[] }> = ({ items }) => {
  const activate = useCrumbActivate();
  return (
    <CollapsedCrumbMenu
      label={`Show ${items.length} more levels`}
      contentClassName="w-[16rem]"
    >
      {items.map((crumb, i) => (
        <DropdownMenuItem
          key={`${crumb.label}-${i}`}
          onSelect={() => activate(crumb)}
          className="gap-1 truncate"
          style={crumbDepthIndent(i)}
        >
          <CrumbLabel emoji={crumb.emoji} label={crumb.label} />
        </DropdownMenuItem>
      ))}
    </CollapsedCrumbMenu>
  );
};

/**
 * Renders a crumb as whatever kind of link it declared — an in-page callback, a
 * workspace resource (which opens in-panel when there is one), the Library root,
 * or a plain route.
 */
const CrumbNavigable: React.FC<{
  crumb: BreadcrumbItem;
  className: string;
  children: React.ReactNode;
}> = ({ crumb, className, children }) => {
  if (crumb.onClick !== undefined) {
    return (
      <button type="button" onClick={crumb.onClick} className={className}>
        {children}
      </button>
    );
  }
  if (crumb.target !== undefined) {
    return (
      <ArtifactResourceLink to={crumb.target} className={className}>
        {children}
      </ArtifactResourceLink>
    );
  }
  if (crumb.opensLibrary === true) {
    // The Library is the root of the tree, not a resource, so it's an
    // in-stack pushView (Back returns here) rather than a new tab.
    return (
      <ArtifactLink to={{ kind: 'library' }} className={className}>
        {children}
      </ArtifactLink>
    );
  }
  if (crumb.href !== undefined) {
    return (
      <Link to={crumb.href} className={className}>
        {children}
      </Link>
    );
  }
  return <span className={className}>{children}</span>;
};

/**
 * Which crumbs stay on the bar, which fold into the `…` menu, and which are
 * dropped outright.
 *
 * `keepRoot` is what a phone turns off. The bar there is ~340px wide and has to
 * hold the nav trigger, the trail and the page's actions, which leaves a trail
 * like `Library › … › Quarterly revenue review` about 60px for the one crumb
 * anyone needs — the page's own name. Folding the root in with the rest buys
 * that name the whole trail, and costs nothing that isn't one tap away in the
 * menu (or in the nav the trigger beside it opens).
 *
 * The narrow path has a second rule, and it is about when the `…` is worth its
 * own width. Folding *one* ancestor is a bad trade: the menu costs about what
 * the crumb it hides would have, so `Chats › Greeting` became `… › Greeting`
 * and saved nothing while turning a readable trail into a puzzle. It is also
 * the case where the hidden crumb matters least — a trail only ever has a
 * single ancestor when that ancestor is a top-level destination (see
 * {@link useResourceBreadcrumbs}: an unfiled resource gets `Chats ›` or
 * `Library ›`, and anything filed in a folder carries the Library root *and*
 * its folders, so it has at least two). Those are one tap away in the nav the
 * hamburger opens, so the bar can simply not spend anything on them.
 *
 * Two or more ancestors means real folders are in the path, which the nav
 * cannot reach — there the `…` earns its width, and it is one control whether
 * the resource sits two folders deep or six.
 *
 * Exported for {@link resource-page-shell.spec.tsx}: the collapse is arithmetic
 * over a path, and testing it through a rendered bar means asserting on a
 * viewport.
 */
export function splitBreadcrumbTrail(
  items: BreadcrumbItem[],
  { keepRoot }: { keepRoot: boolean }
): { collapsed: BreadcrumbItem[]; visible: BreadcrumbItem[] } {
  if (keepRoot) {
    if (items.length <= MAX_VISIBLE_CRUMBS) {
      return { collapsed: [], visible: items };
    }
    return {
      collapsed: items.slice(1, items.length - VISIBLE_TAIL_CRUMBS),
      visible: [items[0]!, ...items.slice(items.length - VISIBLE_TAIL_CRUMBS)],
    };
  }

  const ancestors = items.slice(0, -1);
  const tail = items.slice(-1);
  if (ancestors.length === 0) {
    return { collapsed: [], visible: items };
  }
  // One ancestor: dropped, not folded. Nothing is drawn in its place.
  if (ancestors.length === 1) {
    return { collapsed: [], visible: tail };
  }
  return { collapsed: ancestors, visible: tail };
}

const Breadcrumbs: React.FC<{
  items: BreadcrumbItem[];
  onRenameCurrent?: (name: string) => void;
}> = ({ items, onRenameCurrent }) => {
  // Below `sm` the trail folds down to the page's own name. See
  // {@link splitBreadcrumbTrail}.
  const isNarrow = useIsBelowSm();
  // Kept out of the render below so the key for each rendered crumb can stay
  // tied to the crumb rather than its shifting index — the trail changes length
  // as folder ancestry resolves.
  const { collapsed, visible } = splitBreadcrumbTrail(items, {
    keepRoot: !isNarrow,
  });
  // Where the folded levels sit: back in the path they were cut out of, which
  // is after the root when there is one and at the head of the bar when the
  // root folded away with them.
  const collapsedAt = isNarrow ? 0 : 1;

  return (
    <>
      {visible.map((crumb, i) => {
        const isLast = i === visible.length - 1;
        const key = `${crumb.label}|${
          crumb.href ?? crumb.target?.resourceId ?? i
        }`;
        return (
          <React.Fragment key={key}>
            {i > 0 ? <CrumbSeparator /> : null}
            {i === collapsedAt && collapsed.length > 0 ? (
              <>
                <CollapsedCrumbs items={collapsed} />
                <CrumbSeparator />
              </>
            ) : null}
            {crumb.pending === true ? (
              <Skeleton className="h-3.5 w-20 shrink-0 rounded bg-coco-accent" />
            ) : isLast ? (
              <CurrentCrumb crumb={crumb} onRename={onRenameCurrent} />
            ) : (
              <CrumbNavigable crumb={crumb} className={CRUMB_LINK_CLASS}>
                <CrumbLabel emoji={crumb.emoji} label={crumb.label} />
              </CrumbNavigable>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

/**
 * Shell layout for resource detail pages.
 *
 * Renders a slim top bar with folder-aware breadcrumb navigation and an
 * optional actions slot (share, move, archive buttons, etc.), then fills
 * the remaining height with the page content.
 *
 * Pass `breadcrumbs={undefined}` to show a loading skeleton in the bar.
 */
export const ResourcePageShell: React.FC<{
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  /**
   * Makes the last crumb — the page's own name — click-to-edit, committing on
   * Enter or blur. Omit and the crumb stays plain text. Honoured only on the
   * bar this shell draws: inside the artifact panel the trail belongs to
   * {@link UnifiedPanelBar}, whose leading control is the artifact switcher.
   */
  onRenameCurrent?: (name: string) => void;
  /** Slot rendered left of the breadcrumb (e.g. a drill-in back arrow). */
  leading?: React.ReactNode;
  /**
   * Slot rendered immediately after the trail's last crumb — the page's own
   * name — for a control that belongs to that name rather than to the page.
   *
   * The favorites star is the case this exists for. In the actions cluster it
   * sat at the far right of the bar, a whole trail's width from the thing it
   * marks; here it reads as part of the title, the way it does on a Library
   * row. Kept out of `actions` deliberately: that cluster is right-aligned and
   * `shrink-0`, so anything in it is pinned to the opposite edge.
   *
   * Inside the artifact panel this lands at the end of the artifact switcher
   * and drill-in trail, which is that surface's version of the same place.
   */
  titleAdornment?: React.ReactNode;
  children: React.ReactNode;
  /** `panel` = sidebar-colored bar + rounded content; `flat` = legacy full-width bar (artifact embeds). */
  layout?: ResourcePageShellLayout;
  /** Which ground this page sits on. See {@link ResourcePageSurface}. */
  surface?: ResourcePageSurface;
}> = ({
  breadcrumbs,
  actions,
  onRenameCurrent,
  leading,
  titleAdornment,
  children,
  layout = 'panel',
  surface = 'reader',
}) => {
  const artifactPanel = useArtifactPanel();
  const unifiedChrome = usePanelUnifiedChrome();
  const barAlreadyRendered = useUnifiedBarRendered();
  const cardAlreadyDrawn = useResourcePageCardDrawn();
  const panelBarActionsSlot = usePanelBarActionsSlot();
  const panelBarTitleSlot = usePanelBarTitleSlot();
  // A shell nested inside another resource page renders flat whatever it asked
  // for: the page hosting it has already drawn the card, and a second one is a
  // border inside a border. See {@link useResourcePageCardDrawn}.
  const isFlatLayout = layout === 'flat' || cardAlreadyDrawn;
  const mergedActions = useMemo(() => {
    if (artifactPanel?.canExpand !== true) {
      return actions;
    }
    return (
      <>
        {actions}
        <div className="ml-2">
          <ArtifactPanelExpandButton />
        </div>
      </>
    );
  }, [actions, artifactPanel?.canExpand]);
  const hasActions = mergedActions !== undefined && mergedActions !== null;
  const hasBreadcrumbs =
    breadcrumbs === undefined ||
    (Array.isArray(breadcrumbs) && breadcrumbs.length > 0);
  const hasLeading = leading !== undefined && leading !== null;

  // Inside the artifact panel the panel owns one unified trail
  // (artifact ▾ › pushView…), so this shell renders that single bar in place of
  // its own breadcrumbs and drill-in back arrow — keeping only its own actions,
  // which the bar hosts. A nested shell (bar already claimed) renders bare, and
  // sends its actions up into that one bar rather than losing them.
  const unified = unifiedChrome !== null;
  const ownsUnifiedBar = unified && !barAlreadyRendered;

  // A nested shell draws no bar of its own, which used to drop its actions
  // outright — drilling workflow → agent left the agent with no model selector
  // and no save indicator while you were editing it. Portal them into the one
  // bar the ancestor drew instead of stacking a second row for them.
  const isNested = unified && barAlreadyRendered;
  const nestedActionsPortal =
    isNested &&
    actions !== undefined &&
    actions !== null &&
    panelBarActionsSlot !== null
      ? createPortal(actions, panelBarActionsSlot)
      : null;
  // The adornment goes to the *trail's* slot rather than riding along with the
  // actions. Every resource opened in the panel is a nested shell — the panel
  // drew the bar before the resource mounted — so this is the ordinary case,
  // not an edge one, and sending it to the actions cluster left the star at the
  // far end of the bar among the save state and the model selector.
  const nestedTitlePortal =
    isNested &&
    titleAdornment !== undefined &&
    titleAdornment !== null &&
    panelBarTitleSlot !== null
      ? createPortal(titleAdornment, panelBarTitleSlot)
      : null;

  const showBar = hasActions || hasBreadcrumbs || hasLeading;
  const bar = unified ? null : showBar ? (
    <div
      data-slot="resource-page-shell-bar"
      className="flex items-center gap-2 px-4 h-11 shrink-0 sticky top-0 z-20 bg-background border-b border-border/50"
    >
      <PageBarSidebarTrigger />
      {hasLeading ? (
        <div className="flex items-center shrink-0">{leading}</div>
      ) : null}
      {/* `overflow-hidden`: without it a deep or long trail escapes its box and
          runs under the actions, which are `shrink-0` and hold their width. */}
      <nav
        className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
        aria-label="Breadcrumb"
      >
        {breadcrumbs === undefined ? (
          <BreadcrumbSkeleton />
        ) : (
          <>
            <Breadcrumbs
              items={breadcrumbs}
              onRenameCurrent={onRenameCurrent}
            />
            {titleAdornment}
          </>
        )}
      </nav>

      {hasActions && (
        <div className="flex items-center gap-1 shrink-0">{mergedActions}</div>
      )}
    </div>
  ) : null;

  const scroll = <ContentScrollProvider>{children}</ContentScrollProvider>;
  // `UnifiedPanelBar` renders the bar *and* the content, because it also owns
  // the "bar already claimed" flag and the portal target a nested shell needs.
  // Whatever this shell drew, nothing nested inside it draws a second nav
  // trigger. `UnifiedPanelBar` claims it for its own subtree; this covers the
  // plain bar, and the `md:hidden` fallback, and the `unified` case where an
  // ancestor bar already claimed it and this shell draws nothing at all.
  // Claimed only when this shell actually drew a bar to hold the trigger.
  //
  // It used to claim unconditionally, and a shell with nothing to draw covered
  // for that by rendering a mobile-only strip containing just the hamburger.
  // Where the page below had a bar of its own that cost a whole row: a chat in
  // the artifact focus view passes an empty trail and no actions precisely so
  // that no bar is drawn, and on a phone it got the strip anyway — a 44px band
  // with one hamburger in it, stacked above the artifact's own bar, whose
  // trigger this had claimed away. Two rows of chrome for one row of content.
  //
  // Drawing neither loses nothing. The bar below picks the trigger up inline,
  // and a page with no bar anywhere gets the app shell's strip — see
  // {@link SidebarTriggerPresenceProvider}, which exists for that case and
  // covers it better, since it also catches pages that render no shell at all.
  const drewBar = ownsUnifiedBar || bar !== null;
  const body = (
    <SidebarTriggerClaimedProvider value={drewBar}>
      {ownsUnifiedBar ? (
        <UnifiedPanelBar actions={actions} titleAdornment={titleAdornment}>
          {scroll}
        </UnifiedPanelBar>
      ) : (
        <>
          {bar}
          {nestedActionsPortal}
          {nestedTitlePortal}
          {scroll}
        </>
      )}
    </SidebarTriggerClaimedProvider>
  );

  return (
    <div
      className="h-full w-full flex flex-col min-h-0 overflow-hidden"
      // The ground, stamped here rather than on the card below because a page
      // rendered flat has no card and still sits on one. See
      // {@link ResourcePageSurface}.
      data-surface={surface}
    >
      {/* Linear-style: bar + content are one rounded card floating on the
          sidebar canvas (see .v1-content-panel in v1.css). Flat drops the card
          to `display: contents`, which is layout-transparent, rather than
          dropping the element.

          The element has to stay. Rendering `body` bare in one branch and
          nested in a card in the other puts the page's whole subtree at a
          different depth in each, so React unmounts and rebuilds it on every
          flip — and a chat entering focus flips exactly this (`layout` goes
          `panel` → `flat`). That tore down the artifact panel, its held tabs
          and its open/closed state on the way in, and handed back a freshly
          mounted one on the way out, which is why closing focus landed on the
          artifacts index card instead of the artifact you were reading. */}
      <div
        className={isFlatLayout ? 'contents' : 'v1-content-panel'}
        // Inside the split the panel is the card; this one keeps its layout
        // and drops its chrome rather than framing the same content twice.
        //
        // Not in the focus view, which supplies the same chrome but no
        // card: nothing frames the artifact there, so stripping this left it
        // flush to the window while the very same resource opened from the
        // Library floated in a bordered card. The artifact is the page there,
        // so it should be framed exactly as its own page is.
        {...(!isFlatLayout &&
          unified &&
          unifiedChrome?.focusView === undefined && {
            'data-in-artifact-panel': 'true',
          })}
      >
        {/* Flat defers to whatever an ancestor already claimed, which is what
            rendering no provider at all used to do. */}
        <ResourcePageCardDrawnProvider
          value={isFlatLayout ? cardAlreadyDrawn : true}
        >
          {body}
        </ResourcePageCardDrawnProvider>
      </div>
    </div>
  );
};
